import { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { font } from '../styles/typography';
import { useAuth } from '../modules/auth/context/AuthContext';
import { useBackendDown } from '../utils/backendStatus';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Screen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 16px;
  text-align: center;
  background: var(--spark-color-page);
  color: var(--spark-color-text);
`;

const Logo = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
`;

const Title = styled.h1`
  margin: 8px 0 0;
  ${font('xxl')}
  font-weight: 600;
`;

const Hint = styled.p`
  margin: 0;
  max-width: 420px;
  color: var(--spark-color-text-secondary);
  ${font('base')}
`;

const Spinner = styled.div`
  width: 22px;
  height: 22px;
  margin-top: 8px;
  border-radius: 50%;
  border: 3px solid var(--spark-color-text-muted);
  border-top-color: transparent;
  animation: ${spin} 0.9s linear infinite;
`;

const rise = keyframes`
  from { opacity: 0; transform: translate(-50%, 8px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;

const ping = keyframes`
  0% { transform: scale(0.6); opacity: 0.75; }
  100% { transform: scale(2.1); opacity: 0; }
`;

// Колір стану: помаранчевий — чекаємо на сервер, зелений — з'єднання відновлено.
const accent = ({ $ok }) => ($ok ? 'var(--spark-color-success-text)' : 'var(--spark-color-warning-text)');

const Toast = styled.div`
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 3000;
  display: flex;
  align-items: center;
  gap: 14px;
  width: max-content;
  max-width: calc(100vw - 32px);
  padding: 12px 16px 12px 14px;
  border-radius: 10px;
  background: var(--spark-color-surface);
  color: var(--spark-color-text);
  border: 1px solid var(--spark-color-border);
  border-left: 4px solid ${accent};
  box-shadow: 0 8px 28px var(--spark-color-shadow-strong, rgba(0, 0, 0, 0.35));
  animation: ${rise} 0.22s ease-out;
  transition: border-left-color 0.3s;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Signal = styled.span`
  position: relative;
  flex: none;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  color: ${accent};
  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: currentColor;
  }
  ${({ $ok }) => !$ok && css`
    &::after {
      content: '';
      position: absolute;
      inset: 3px;
      border-radius: 50%;
      border: 2px solid currentColor;
      animation: ${ping} 1.6s ease-out infinite;
    }
  `}
  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; opacity: 0.4; }
  }
`;

const Check = styled.svg`
  width: 22px;
  height: 22px;
  flex: none;
  color: ${accent};
`;

const ReloadButton = styled.button`
  flex: none;
  margin-left: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid var(--spark-color-success-text);
  background: transparent;
  color: var(--spark-color-success-text);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: var(--spark-color-success-surface); }
  &:focus-visible { outline: 2px solid var(--spark-color-success-text); outline-offset: 2px; }
`;

const ToastText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ToastTitle = styled.strong`
  ${font('base')}
  font-weight: 600;
`;

const ToastHint = styled.span`
  color: var(--spark-color-text-secondary);
  ${font('md')}
`;

// Скільки показувати «З'єднання відновлено» з кнопкою оновлення (мс).
const RESTORED_VISIBLE_MS = 12000;
// Пауза перед автооновленням, щоб встигнути прочитати повідомлення (мс).
const AUTO_RELOAD_DELAY_MS = 1200;

// Плашка посеред роботи: не перекриває сторінку. Після відновлення на мить стає
// зеленою — щоб було видно, що саме сталося, а не просто зникла.
function BackendToast({ down }) {
  // restored: null — плашки нема; 'auto' — сторінку оновимо самі; 'manual' — є ввід користувача
  const [restored, setRestored] = useState(null);
  const wasDown = useRef(false);
  const typed = useRef(false);

  // Поки бекенд недоступний, помічаємо, чи користувач щось вводив (форма, коментар):
  // у такому разі сторінку самі не оновлюємо — лише пропонуємо кнопку.
  useEffect(() => {
    if (!down) return undefined;
    typed.current = false;
    const mark = () => { typed.current = true; };
    document.addEventListener('input', mark, true);
    return () => document.removeEventListener('input', mark, true);
  }, [down]);

  useEffect(() => {
    if (down) {
      wasDown.current = true;
      setRestored(null);
      return undefined;
    }
    if (!wasDown.current) return undefined;
    wasDown.current = false;
    if (!typed.current) {
      // сторінки, що не завантажили дані, поки бекенда не було, самі не повторюють запит
      setRestored('auto');
      const t = setTimeout(() => window.location.reload(), AUTO_RELOAD_DELAY_MS);
      return () => clearTimeout(t);
    }
    setRestored('manual');
    const t = setTimeout(() => setRestored(null), RESTORED_VISIBLE_MS);
    return () => clearTimeout(t);
  }, [down]);

  if (!down && !restored) return null;
  const ok = !down;
  return (
    <Toast role="status" aria-live="polite" $ok={ok}>
      {ok ? (
        <Check $ok viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M7.5 12.5l3 3 6-6.5" />
        </Check>
      ) : (
        <Signal aria-hidden="true" />
      )}
      <ToastText>
        <ToastTitle>{ok ? "З'єднання відновлено" : "Немає зв'язку з сервером"}</ToastTitle>
        <ToastHint>
          {!ok && 'Підключаємось знову. Незбережені зміни можуть не зберегтися.'}
          {ok && restored === 'auto' && 'Оновлюємо сторінку…'}
          {ok && restored === 'manual' && 'Ви щось вводили, тому сторінку не чіпаємо. Якщо там помилка або порожні дані — оновіть її.'}
        </ToastHint>
      </ToastText>
      {ok && restored === 'manual' && (
        <ReloadButton type="button" onClick={() => window.location.reload()}>
          Оновити сторінку
        </ReloadButton>
      )}
    </Toast>
  );
}

/**
 * Бекенд недоступний (стартує або перезапускається): фронт завантажився, а /auth/me
 * не відповів JSON. AuthContext повторює запит сам — сторінку перезавантажувати не
 * потрібно, екран зникне, щойно бекенд відповість.
 */
export default function BackendUnavailableScreen() {
  const { backendUnavailable } = useAuth();
  const backendDown = useBackendDown();
  // Посеред роботи не перекриваємо сторінку (можуть бути незбережені дані) — лише плашка.
  if (!backendUnavailable) return <BackendToast down={backendDown} />;
  return (
    <Screen role="alert" aria-live="polite">
      <Logo src="/logo.png" alt="SPARK" />
      <Title>Система тимчасово недоступна</Title>
      <Hint>
        Сервер запускається або оновлюється. Сторінка підключиться автоматично,
        щойно він буде готовий — перезавантажувати її не потрібно.
      </Hint>
      <Spinner aria-hidden="true" />
    </Screen>
  );
}
